import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IEbayLocationsResponse, IEbayPoliciesResponse } from "../models/EbayOffer";

export interface IUseEbayOptions {
  /** Fetch the business policies. Turn off when the caller only needs locations. */
  policies?: boolean;
  /** Fetch the inventory locations. Turn off when the caller only needs policies. */
  locations?: boolean;
  /** Gate both fetches, e.g. on a modal's `open`, so an always-mounted caller fetches only when shown. */
  enabled?: boolean;
}

// Every request here is a live eBay call server-side and the data hardly ever changes, so hold it
// for a full hour (cacheTime must match staleTime or the entry is dropped 5 minutes after the last
// subscriber unmounts) and don't re-read it just because the tab regained focus.
const ONE_HOUR = 1000 * 60 * 60;

export default function useEbay({ policies: wantPolicies = true, locations: wantLocations = true, enabled = true }: IUseEbayOptions = {}) {
  const { get } = useApiHelper();

  const policies = useQuery({
    queryKey: ["ebayPolicies"],
    queryFn: () => get<IEbayPoliciesResponse>("/ebay/policies"),
    enabled: enabled && wantPolicies,
    staleTime: ONE_HOUR,
    cacheTime: ONE_HOUR,
    refetchOnWindowFocus: false,
  });

  const locations = useQuery({
    queryKey: ["ebayLocations"],
    queryFn: () => get<IEbayLocationsResponse>("/ebay/locations"),
    enabled: enabled && wantLocations,
    staleTime: ONE_HOUR,
    cacheTime: ONE_HOUR,
    refetchOnWindowFocus: false,
  });

  return {
    policies,
    locations,
  };
}
